import {
  useTableSortingAndPagination,
  useTablesSortingAndPagination,
} from "#redux/reducers/tableStateReducer";
import {
  WorkflowsTableFilterArea,
  WorkflowsTableTitleDecorationFilterArea,
  type WorkflowsViewFiltersType,
} from "#src/batteries-included-components/FilterAreas/WorkflowsFilterAreas";
import {
  WorkflowsTableFilterSchemaKeys,
  WorkflowsTableFiltersType,
} from "#src/batteries-included-components/FilterAreas/WorkflowsFilterAreas/WorkflowsFilterAreas.helpers";
import { WorkflowStatusToPillVariantMap } from "#src/batteries-included-components/Panels/TablePanels/WorkflowsTablePanel/WorkflowsTablePanel.helpers";
import { RoutingLink } from "#src/batteries-included-components/RoutingLink";
import { useListWorkflows } from "#src/components/hooks/adapters/useWorkflows";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { linkToFacilityDetail } from "#src/routes/organization/facilities/[facilityId]";
import { WorkflowDetailsRoutePath } from "#src/routes/workflows/all/[workflowId]/detail";
import { WorkflowCategoryDetailsRoutePath } from "#src/routes/workflows/categories/[workflowCategoryId]";
import { WorkflowTemplateDetailsRoutePath } from "#src/routes/workflows/templates/[workflowTemplateId]";
import { linkToAssetDetailPage } from "#src/utils/links";
import {
  useDataTableColumnConfigurationProps,
  useDataTableGroupBy,
  useDataTableGroupByProps,
  useDataTableMetaHeaders,
  useDataTableUserInterfaceConfiguration,
} from "#src/utils/table";
import { useQueries } from "@tanstack/react-query";
import {
  DataTable,
  DataTablePanel,
  getDataTableGroupByKeysForGroupByData,
  HeaderType,
  Pill,
  StorageKeys,
} from "@validereinc/common-components";
import {
  AssetType,
  Resources,
  SortDirection,
  WorkflowAdapter,
  WorkflowType,
  type GroupedByEntryType,
} from "@validereinc/domain";
import {
  Assets,
  AssetTypeSelectionType,
} from "@validereinc/domain-controllers";
import { toFlattenedObject, toTitleCaseString } from "@validereinc/utilities";
import { isToday } from "date-fns";
import React, { useMemo } from "react";

const { AssetTypeSelection } = Assets.view;

export type WorkflowsTablePanelProps = {
  /** certain columns are turned off when this table panel is used within the workflow template details page */
  isWithinWorkflowTemplateDetails?: boolean;
  /** optionally bypass filters set in session storage which are fetched through useFilters() */
  overrideFilters?: {
    templateId?: string;
  };
} & StorageKeys;

export const WorkflowsTablePanel = ({
  isWithinWorkflowTemplateDetails = false,
  overrideFilters,
  tableConfigStorageKey,
  tableStateStorageKey,
  viewConfigStorageKey,
  filterConfigStorageKey,
  groupByTablesStateStorageKey,
  defaultAssetType,
}: WorkflowsTablePanelProps & {
  defaultAssetType: AssetTypeSelectionType;
}) => {
  const metaHeaders = useDataTableMetaHeaders();
  const {
    saveDefaultConfigurationMutation,
    isSaveConfigurationAvailable,
    resetConfigurationMutation,
    deleteDefaultConfigurationMutation,
    isDeleteConfigurationAvailable,
    isLoading: isLoadingTableUserInterfaceConfiguration,
    matchingDefaultConfiguration,
    matchingDefaultTableUserInterfaceConfigurationsQuery,
  } = useDataTableUserInterfaceConfiguration(tableConfigStorageKey);
  const [tableFilters] = useStickyState<WorkflowsTableFiltersType>(
    {} as WorkflowsTableFiltersType,
    filterConfigStorageKey
  );
  const [viewFilters] = useStickyState<WorkflowsViewFiltersType>(
    {} as WorkflowsViewFiltersType,
    viewConfigStorageKey
  );
  const { localize } = useLocalization();
  const { categoryId, templateId } = viewFilters;
  const {
    [WorkflowsTableFilterSchemaKeys.date_range]: dateRange,
    [WorkflowsTableFilterSchemaKeys.status]: status,
    [WorkflowsTableFilterSchemaKeys.name]: name,
  } = tableFilters;

  const filters = useMemo(
    () => ({
      created_at: dateRange,
      ...(status ? { status } : {}),
      "workflow_category.id": categoryId,
      "workflow_template.id": overrideFilters?.templateId ?? templateId,
      ...(name ? { name } : {}),
      ...getAssetFilters({
        ...tableFilters,
        ...viewFilters,
      }),
    }),
    [
      dateRange,
      status,
      categoryId,
      templateId,
      name,
      tableFilters,
      viewFilters,
      overrideFilters,
    ]
  );

  const [tableState, updateTableState] = useTableSortingAndPagination(
    {
      sortBy: "created_at",
      sortDirection: SortDirection.DESCENDING,
    },
    filters,
    tableStateStorageKey
  );

  const reqPayload: Parameters<typeof WorkflowAdapter.getList>[0] = {
    page: tableState.page,
    pageSize: tableState.pageSize,
    sortBy: tableState.sortBy,
    sortDirection: tableState.sortDirection,
    groupBy: tableState.groupBy,
    filters,
  };

  const { data, isLoading } = useListWorkflows(reqPayload);
  const items = data?.data ?? [];

  const groupByKeys = getDataTableGroupByKeysForGroupByData(
    items as unknown as GroupedByEntryType[]
  );
  const [groupByTablesStates, updateGroupByTablesStates] =
    useTablesSortingAndPagination(
      groupByKeys,
      {
        ...reqPayload,
        page: 1,
        pageSize: 10,
        sortBy: "created_at",
        sortDirection: SortDirection.DESCENDING,
      },
      groupByTablesStateStorageKey
    );

  const {
    queryPayloads: groupByQueryPayloads,
    setActiveGroupByGroups,
    totalSelectableItems,
  } = useDataTableGroupBy(
    items as unknown as GroupedByEntryType[],
    tableState.groupBy,
    groupByKeys.map((key) => groupByTablesStates.get(key) ?? reqPayload)
  );

  const workflowsGroupedByQueries = useQueries({
    queries: groupByQueryPayloads.map((d) => ({
      queryKey: [Resources.WORKFLOW, "getList", d.payload],
      queryFn: () => WorkflowAdapter.getList(d.payload),
      enabled: d.enabled,
    })),
  });

  const headers = useMemo<Array<HeaderType<WorkflowType>>>(
    () => [
      {
        label: "Name",
        key: "id",
        isSortable: false,
        renderComponent: ({ item }: { item: WorkflowType }) => {
          return (
            <RoutingLink
              to={WorkflowDetailsRoutePath.toLinkParts({
                pathParams: {
                  workflowId: item.id,
                },
              })}
            >
              {item.name}
            </RoutingLink>
          );
        },
      },
      {
        label: "Status",
        key: "status",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <Pill
            variant={WorkflowStatusToPillVariantMap[item.status] || "default"}
          >
            {toTitleCaseString(item.status, {
              intent: "ui",
              exceptions: ["oil-and-gas"],
            })}
          </Pill>
        ),
      },
      {
        label: "Due Date",
        key: "due_date",
        isSortable: true,
        isGroupable: true,
        renderComponent: ({ item }) => (
          <DataTable.DataRow.DateCell
            value={item.due_date}
            withTime={!!(item.due_date && isToday(new Date(item.due_date)))}
            convertToUTC={false}
          />
        ),
      },
      {
        label: "Asset",
        isSortable: false,
        key: "asset.name",
        renderComponent: ({ item }: { item: WorkflowType }) =>
          item.asset ? (
            <RoutingLink
              to={linkToAssetDetailPage(item.asset.asset_type, item.asset.id)}
            >
              {item.asset.name}
            </RoutingLink>
          ) : (
            "-"
          ),
      },
      {
        label: "Asset Type",
        isSortable: false,
        key: "asset.type",
        renderComponent: ({ item }: { item: WorkflowType }) =>
          item.asset ? localize(item.asset.asset_type) : "-",
      },
      {
        label: `Associated ${localize("facility")}`,
        isSortable: false,
        key: "facility.id",
        isGroupable: true,
        renderComponent: ({ item }: { item: WorkflowType }) =>
          item.asset &&
          item.asset.asset_type === AssetType.FACILITY &&
          item.facility ? (
            <RoutingLink to={linkToFacilityDetail(item.facility_id ?? "")}>
              {item.facility.name}
            </RoutingLink>
          ) : (
            "-"
          ),
      },
      ...(!isWithinWorkflowTemplateDetails
        ? [
            {
              label: "Category",
              key: "workflow_template.workflow_category.name",
              isSortable: false,
              renderComponent: ({ item }: { item: WorkflowType }) => (
                <RoutingLink
                  to={WorkflowCategoryDetailsRoutePath.toLinkParts({
                    pathParams: {
                      workflowCategoryId:
                        item.workflow_template.workflow_category.id,
                    },
                  })}
                >
                  {item.workflow_template.workflow_category.name}
                </RoutingLink>
              ),
            },
            {
              label: "Template",
              key: "workflow_template_id",
              isSortable: false,
              isGroupable: true,
              groupByKeys: ["workflow_template_id", "workflow_template.name"],
              groupBySortByKey: "workflow_template.name",
              renderComponent: ({ item }: { item: WorkflowType }) =>
                item.workflow_template?.name ? (
                  <RoutingLink
                    to={WorkflowTemplateDetailsRoutePath.toLinkParts({
                      pathParams: {
                        workflowTemplateId: item.workflow_template_id,
                      },
                    })}
                  >
                    {item.workflow_template.name}
                  </RoutingLink>
                ) : (
                  "-"
                ),
            },
          ]
        : []),
      ...metaHeaders,
    ],
    [isWithinWorkflowTemplateDetails, metaHeaders]
  );

  const groupByDataTableProps = useDataTableGroupByProps({
    items,
    groupBy: tableState.groupBy,
    updateTableState,
    setActiveGroupByGroups,
    groupByQueries: workflowsGroupedByQueries,
    groupByTablesStates,
    updateGroupByTablesStates,
    headers,
    getItemId: (item) => item.id,
    totalSelectableItems,
  });
  const columnConfigurationDataTableProps =
    useDataTableColumnConfigurationProps({
      configurationName: "Default for Workflows list table",
      saveDefaultConfigurationMutation,
      isSaveConfigurationAvailable,
      resetConfigurationMutation,
      deleteDefaultConfigurationMutation,
      isDeleteConfigurationAvailable,
      isLoading: isLoadingTableUserInterfaceConfiguration,
      matchingDefaultConfiguration,
      matchingDefaultTableUserInterfaceConfigurationsQuery,
    });

  return (
    <DataTablePanel
      storageKey={tableConfigStorageKey}
      panelProps={{
        title: "Workflows",
        isFluidY: false,
        titleDecorator: (
          <WorkflowsTableTitleDecorationFilterArea
            filterConfigStorageKey={filterConfigStorageKey}
          />
        ),
      }}
      filterComponent={
        <WorkflowsTableFilterArea
          viewConfigStorageKey={viewConfigStorageKey}
          filterConfigStorageKey={filterConfigStorageKey}
          defaultAssetType={defaultAssetType}
        />
      }
      columnConfigurationProps={columnConfigurationDataTableProps}
      dataTableProps={{
        intent: "simplicity-first",
        headers,
        items: data?.data ?? [],
        getItemId: (item) => item.id,
        isLoading,
        sorting: {
          sortBy: tableState.sortBy,
          sortDirection: tableState.sortDirection,
        },
        pagination: {
          page: tableState.page,
          pageSize: tableState.pageSize,
          total: data?.total_entries,
        },
        onSortChange: updateTableState,
        onPaginationChange: updateTableState,
      }}
      {...groupByDataTableProps}
    />
  );
};

function getAssetFilters({
  assetType,
  assetName,
  facilityId,
  equipmentId,
  deviceId,
  flowId,
  assetGroupId,
  workflow,
}: Partial<WorkflowsTableFiltersType & WorkflowsViewFiltersType>) {
  const isValidId = (assetIds: string[] | undefined) => !!assetIds?.length;

  switch (assetType) {
    case AssetTypeSelection.FACILITY:
      return {
        ...(assetName ? { "facility.name": assetName } : {}),
        ...(isValidId(facilityId)
          ? { "facility.id": facilityId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.facility
          ? toFlattenedObject(workflow.facility, {
              prefix: "facility",
            })
          : {}),
      };
    case AssetTypeSelection.EQUIPMENT:
      return {
        ...(assetName ? { "equipment.name": assetName } : {}),
        ...(isValidId(equipmentId)
          ? { "equipment.id": equipmentId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.equipment
          ? toFlattenedObject(workflow.equipment, {
              prefix: "equipment",
            })
          : {}),
      };
    case AssetTypeSelection.DEVICE:
      return {
        ...(assetName ? { "device.name": assetName } : {}),
        ...(isValidId(deviceId)
          ? { "device.id": deviceId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.device
          ? toFlattenedObject(workflow.device, {
              prefix: "device",
            })
          : {}),
      };
    case AssetTypeSelection.FLOW:
      return {
        ...(assetName ? { "flow.name": assetName } : {}),
        ...(isValidId(flowId)
          ? { "flow.id": flowId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.flow
          ? toFlattenedObject(workflow.flow, {
              prefix: "flow",
            })
          : {}),
      };
    case AssetTypeSelection.ASSET_GROUP:
      return {
        ...(assetName ? { "asset_group.name": assetName } : {}),
        ...(isValidId(assetGroupId)
          ? { "asset_group.id": assetGroupId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.asset_group
          ? toFlattenedObject(workflow.asset_group, {
              prefix: "asset_group",
            })
          : {}),
      };
    default:
      return {};
  }
}
